import { createContext, useContext, useEffect, type RefObject } from 'react'
import type {
  DomainKnowledgeReference,
  ReqGuardCoverageScores,
  ReqGuardSnapshot,
  ReqGuardTab,
  RequirementCheckResult,
  WizardSnapshot,
} from '@cypremos/shared'

type Fail = { ok: false; error: string }

/** A requirement check with the classified requirements its entry indexes refer to. */
export interface RequirementCheckEvidence {
  check: RequirementCheckResult
  requirements: string[]
}

/** What generated test cases rest on: their 「仮定」 and the knowledge items they cite. */
export interface GenerationEvidence {
  reference?: DomainKnowledgeReference
  cases: { id: string; name: string; assumptions: string[]; knowledgeItems: { id: string; title: string }[] }[]
}

/**
 * What the vECU test generator wizard exposes to the floating Chat. Each method runs
 * the wizard's own existing handler, so the wizard visibly advances exactly as if
 * the user had pressed the button. Test execution is intentionally not exposed.
 */
export interface WizardBridgeHandle {
  getSnapshot(): WizardSnapshot
  /** Runs Step 1 → 2. `requirements` (verbatim, already checked) replaces the Step 1 text first. */
  classify(requirements?: string): Promise<{ ok: true; requirementCount: number; overallCategory: string; revision: number } | Fail>
  /** The background requirement check of the latest classification; null when skipped or failed. */
  getRequirementCheck(): Promise<RequirementCheckEvidence | null>
  /** assumptionCount: generated cases that state assumptions (all are selected afterwards). */
  generateTestCases(): Promise<{ ok: true; testCaseIds: string[]; suspension: boolean; assumptionCount: number; evidence: GenerationEvidence } | Fail>
  generateRobotScript(options?: { feedback?: string }): Promise<{ ok: true } | Fail>
  regenerateTestCase(testCaseId: string, feedback: string): Promise<{ ok: true; name: string } | Fail>
  addTestCase(description: string): Promise<{ ok: true; name: string } | Fail>
}

/** A coverage round as the Chat reports it: numbers computed by the page, never by the model. */
export interface CoverageRunSummary {
  roundIndex: number
  scores: ReqGuardCoverageScores
  /** The previous round's overall score, when this round came from applying proposals. */
  previousOverall?: number | null
  gapCount: number
  proposalCount: number
  acceptedCount: number
}

/** A finished 要求品質レビュー analysis as the Chat reports it (scores from the wizard, demonstration data). */
export interface ReviewRunSummary {
  analyzed: number
  averageScore: number | null
  issueCount: number
  /** Lowest-scored requirements first. */
  lowest: { id: string; score: number }[]
}

/**
 * What the Requirement Doctor page (/req-guard) exposes to the floating Chat. Each method
 * runs the page's own handlers, so the page visibly moves exactly as if the user had used
 * it. Applying proposals is only reachable through the Chat's human confirm card.
 */
export interface ReqGuardBridgeHandle {
  getSnapshot(): ReqGuardSnapshot
  openTab(tab: ReqGuardTab): void
  /**
   * Opens 規格カバレッジ and runs it on what is in its input, on `spec` (verbatim, already
   * checked) or on the tab's own sample spec.
   */
  runCoverage(input: { spec?: string; sample?: boolean }): Promise<{ ok: true; summary: CoverageRunSummary } | Fail>
  setProposalDecisions(accept: string[], reject: string[]): { ok: true; acceptedCount: number; proposalCount: number } | Fail
  /** Applies the accepted proposals and re-reviews (the page's 「反映して再レビュー」). */
  applyCoverage(): Promise<{ ok: true; summary: CoverageRunSummary } | Fail>
  /** 要求品質レビュー: imports `text` (verbatim, already checked; one requirement per line) or the wizard's sample. */
  importRequirements(input: { text?: string; sample?: boolean }): { ok: true; count: number } | Fail
  /** 要求品質レビュー: selects `ids` (empty = all), analyzes them and resolves when the analysis finished. */
  analyzeRequirements(ids: string[]): Promise<{ ok: true; summary: ReviewRunSummary } | Fail>
  openRequirement(id: string): { ok: true } | Fail
  /** Opens the fix screen; the person chooses and applies the fixes there. */
  startFix(id: string): { ok: true } | Fail
  /** Ticks exactly these requirements on the selection screen. */
  selectRequirements(ids: string[]): { ok: true } | Fail
  /** Opens the page's own delete confirmation; nothing is deleted until the person confirms there. */
  openDeleteConfirmation(ids: string[]): { ok: true } | Fail
  /** Opens the page's own force-approval dialog; nothing is approved until the person confirms there. */
  openForceApproval(ids: string[]): { ok: true } | Fail
}

export interface OpenChatRequest {
  seq: number
  notice?: string
}

export interface ChatActionBridgeValue {
  /** Latest handle of the mounted wizard, or null when no wizard is on screen. */
  getWizard(): WizardBridgeHandle | null
  wizardAttached: boolean
  /** The wizard's Mock/Live toggle. Meaningful only while attached. */
  wizardUseMock: boolean
  registerWizard(handle: RefObject<WizardBridgeHandle>, useMock: boolean): () => void
  /** Latest handle of the mounted Requirement Doctor page, or null when it is not on screen. */
  getReqGuard(): ReqGuardBridgeHandle | null
  reqGuardAttached: boolean
  /** The page's Live/Mock badge. Meaningful only while attached. */
  reqGuardUseMock: boolean
  registerReqGuard(handle: RefObject<ReqGuardBridgeHandle>, useMock: boolean): () => void
  chatOpen: boolean
  setChatOpen(open: boolean): void
  openRequest: OpenChatRequest | null
  requestOpenChat(notice?: string): void
}

export const ChatActionBridgeContext = createContext<ChatActionBridgeValue | null>(null)

/** Null outside the provider (e.g. isolated component tests) — callers fall back to docs-only chat. */
export function useChatActionBridge(): ChatActionBridgeValue | null {
  return useContext(ChatActionBridgeContext)
}

/**
 * Registers the wizard with the Chat for as long as it is mounted. Pass a ref whose
 * `.current` is reassigned every render so calls always reach the latest handlers.
 */
export function useRegisterWizard(handle: RefObject<WizardBridgeHandle>, useMock: boolean) {
  const bridge = useChatActionBridge()
  const register = bridge?.registerWizard
  useEffect(() => {
    if (!register) return
    return register(handle, useMock)
  }, [register, handle, useMock])
}

/** Registers the Requirement Doctor page with the Chat for as long as it is mounted (see useRegisterWizard). */
export function useRegisterReqGuard(handle: RefObject<ReqGuardBridgeHandle>, useMock: boolean) {
  const bridge = useChatActionBridge()
  const register = bridge?.registerReqGuard
  useEffect(() => {
    if (!register) return
    return register(handle, useMock)
  }, [register, handle, useMock])
}
